import type { FC } from 'react'
import { Box, Typography } from '@mui/material'
import { DefaultModal } from '@/shared/components/DefaultModal'
import { ButtonPrimary, Button } from '@/interaxDesignSystemComponents'
import { INTERAX_COLORS } from '@/constants/colors'
import { useTranslation } from '@/shared/hooks/useIntractionTranslate'
import type { Report } from 'pure-js/types/Report'

export interface DeleteReportModalProps {
  isOpen: boolean
  onClose: () => void
  report: Report | null
  onConfirm: () => Promise<void>
  isDeleting?: boolean
}

export const DeleteReportModal: FC<DeleteReportModalProps> = ({
  isOpen,
  onClose,
  report,
  onConfirm,
  isDeleting = false
}) => {
  const { t } = useTranslation()

  const handleConfirm = async () => {
    try {
      await onConfirm()
      onClose()
    } catch (error) {
      console.error('Failed to delete report:', error)
    }
  }

  return (
    <DefaultModal
      isOpen={isOpen}
      onClose={onClose}
      maxWidth="500px"
      backgroundColor={INTERAX_COLORS.heritageBlue}
      showCloseButton={!isDeleting}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Title */}
        <Typography
          variant="h5"
          sx={{
            color: INTERAX_COLORS.white,
            textAlign: 'center',
            fontWeight: 600
          }}
        >
          {t('facilitator.reportManagement.deleteTitle')}
        </Typography>

        {/* Message */}
        <Typography
          variant="body1"
          sx={{
            color: INTERAX_COLORS.thunderGrey,
            textAlign: 'center'
          }}
        >
          {report
            ? t('facilitator.reportManagement.deleteMessageWithTitle', {
                title: report.title || t('facilitator.reportManagement.untitledReport')
              })
            : t('facilitator.reportManagement.deleteMessage')}
        </Typography>

        {/* Actions */}
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            justifyContent: 'flex-end',
            marginTop: 2
          }}
        >
          <Button variant="secondary" onClick={onClose} disabled={isDeleting}>
            {t('facilitator.reportManagement.deleteCancel')}
          </Button>
          <ButtonPrimary
            onClick={handleConfirm}
            disabled={isDeleting}
            className="!bg-red-600 hover:!bg-red-700 focus:ring-red-500/20 border-red-600"
          >
            {isDeleting ? t('facilitator.reportManagement.deleting') : t('facilitator.reportManagement.deleteConfirm')}
          </ButtonPrimary>
        </Box>
      </Box>
    </DefaultModal>
  )
}
